import { medcheckPrescriptionTrainingDatabase } from '../data/medcheckPrescriptionTrainingDatabase';
import type { Medicine } from '../types';

interface TrainingMedicineEntry {
  brand: string;
  aliases?: readonly string[];
  generic: string;
  form?: string;
  category?: string;
  source?: string;
}

interface TrainingPrescriptionMedicine {
  rawLine?: string;
  brandName?: string;
  genericName?: string;
  form?: string;
  strength?: string;
  dose?: string;
  frequencyPattern?: string;
  foodTiming?: string;
  duration?: string;
  englishInstruction?: string;
  banglaInstruction?: string;
  confidence?: number;
  needsReview?: boolean;
  reviewReason?: string;
}

interface TrainingPrescription {
  medicines?: TrainingPrescriptionMedicine[];
}

interface TrainingDatabase {
  medicineKnowledgeBase?: readonly TrainingMedicineEntry[];
  prescriptions?: readonly TrainingPrescription[];
}

export interface TrainingMedicineMatch {
  status: 'identified' | 'needs_review' | 'possible_lookalike';
  confidence: number;
  normalizedName: string;
  entry?: TrainingMedicineEntry;
  prescriptionReference?: TrainingPrescriptionMedicine;
  possibleMatches: string[];
  reason?: string;
}

const DB = medcheckPrescriptionTrainingDatabase as unknown as TrainingDatabase;
const FORM_PREFIX_PATTERN = /^(TAB|TABLET|CAP|CAPSULE|INJ|INJECTION|SYP|SYRUP|SUSP|DROP|DROPS|E\/D|N\/D|CREAM|OINT|NEB|NEBULIZATION)\.?\s+/i;
const FORM_WORD_PATTERN = /\b(TAB|TABLET|CAP|CAPSULE|INJ|INJECTION|SYP|SYRUP|SUSP|DROP|DROPS|CREAM|OINT|NEB|NEBULIZATION|WITH)\b/gi;
const STRENGTH_PATTERN = /\b\d+(\.\d+)?\s?(MG|MCG|G|ML|IU|UNIT|UNITS|%)\b/gi;
const UNKNOWN_VALUES = new Set(['', 'unknown', 'not specified', 'needs review', 'needs verification', 'n/a', 'na']);

function compact(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function cleanValue(value?: string): string {
  return (value || '').trim();
}

function hasMeaningfulValue(value?: string): boolean {
  return !UNKNOWN_VALUES.has(cleanValue(value).toLowerCase());
}

export function normalizeMedicineName(name: string): string {
  return cleanValue(name)
    .toUpperCase()
    .replace(/[^\w\s+./%-]/g, ' ')
    .replace(FORM_PREFIX_PATTERN, '')
    .replace(FORM_WORD_PATTERN, ' ')
    .replace(STRENGTH_PATTERN, ' ')
    .replace(/\b(SR|XR|MR|CR|DS)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function editDistance(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, () => Array<number>(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i += 1) dp[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) dp[0][j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }

  return dp[a.length][b.length];
}

function getCandidateNames(entry: TrainingMedicineEntry): string[] {
  return [entry.brand, ...(entry.aliases || [])].filter(Boolean);
}

function findPrescriptionReference(brandName: string): TrainingPrescriptionMedicine | undefined {
  const key = compact(normalizeMedicineName(brandName));
  return (DB.prescriptions || [])
    .flatMap((prescription) => prescription.medicines || [])
    .find((medicine) => compact(normalizeMedicineName(medicine.brandName || '')) === key);
}

function devLog(message: string, details?: unknown) {
  if (!import.meta.env.DEV) return;
  console.info(`[MedCheck Resolver] ${message}`, details || '');
}

export function resolveMedicineFromTrainingDb(name: string, confidence?: number): TrainingMedicineMatch {
  const normalizedName = normalizeMedicineName(name);
  const key = compact(normalizedName);

  if (!key || /unclear|illegible|unknown|\?/i.test(name) || (confidence !== undefined && confidence < 0.45)) {
    return {
      status: 'needs_review',
      confidence: confidence || 0,
      normalizedName,
      possibleMatches: [],
      reason: 'Unclear or low-confidence medicine name',
    };
  }

  const entries = DB.medicineKnowledgeBase || [];
  for (const entry of entries) {
    const exactOrAlias = getCandidateNames(entry).find((candidate) => compact(normalizeMedicineName(candidate)) === key);
    if (exactOrAlias) {
      const reference = findPrescriptionReference(entry.brand);
      devLog('Matched training DB alias', { input: name, brand: entry.brand, alias: exactOrAlias });
      return {
        status: 'identified',
        confidence: 0.98,
        normalizedName,
        entry,
        prescriptionReference: reference,
        possibleMatches: [],
      };
    }
  }

  const containsMatches = entries.filter((entry) =>
    getCandidateNames(entry).some((candidate) => {
      const candidateKey = compact(normalizeMedicineName(candidate));
      return key.length >= 5 && candidateKey.length >= 5 && (key.includes(candidateKey) || candidateKey.includes(key));
    })
  );

  if (containsMatches.length === 1) {
    const entry = containsMatches[0];
    devLog('Matched training DB contained brand', { input: name, brand: entry.brand });
    return {
      status: 'identified',
      confidence: 0.93,
      normalizedName,
      entry,
      prescriptionReference: findPrescriptionReference(entry.brand),
      possibleMatches: [],
    };
  }

  const fuzzy = entries
    .map((entry) => {
      const best = getCandidateNames(entry)
        .map((candidate) => {
          const candidateKey = compact(normalizeMedicineName(candidate));
          const distance = editDistance(key, candidateKey);
          const similarity = 1 - distance / Math.max(key.length, candidateKey.length, 1);
          return { candidate, distance, similarity };
        })
        .sort((a, b) => b.similarity - a.similarity || a.distance - b.distance)[0];
      return { entry, ...best };
    })
    .filter((candidate) => key.length >= 5 && candidate.similarity >= 0.82)
    .sort((a, b) => b.similarity - a.similarity);

  if (fuzzy.length > 0) {
    const best = fuzzy[0];
    const close = fuzzy.filter((candidate) => best.similarity - candidate.similarity < 0.04);

    if (close.length === 1 && best.similarity >= 0.93) {
      devLog('Accepted high-confidence fuzzy training DB match', { input: name, brand: best.entry.brand, confidence: best.similarity });
      return {
        status: 'identified',
        confidence: best.similarity,
        normalizedName,
        entry: best.entry,
        prescriptionReference: findPrescriptionReference(best.entry.brand),
        possibleMatches: [],
      };
    }

    return {
      status: 'possible_lookalike',
      confidence: best.similarity,
      normalizedName,
      possibleMatches: close.map((candidate) => candidate.entry.brand).slice(0, 4),
      reason: 'Possible look-alike medicine name',
    };
  }

  return {
    status: 'needs_review',
    confidence: confidence || 0.6,
    normalizedName,
    possibleMatches: [],
    reason: 'Brand not found in training database',
  };
}

export function applyTrainingDbMedicineMatch(medicine: Medicine): Medicine {
  const match = resolveMedicineFromTrainingDb(medicine.name, medicine.confidence);

  if (match.status !== 'identified' || !match.entry) {
    if (match.status !== 'identified') {
      devLog('Training DB unresolved medicine', { input: medicine.name, reason: match.reason, matches: match.possibleMatches });
    }
    return {
      ...medicine,
      identificationStatus: match.status,
      resolutionConfidence: Math.max(medicine.resolutionConfidence || 0, match.confidence),
      possibleMatches: Array.from(new Set([...(medicine.possibleMatches || []), ...match.possibleMatches])),
      notes: [medicine.notes, match.reason].filter(Boolean).join(' '),
    };
  }

  const reference = match.prescriptionReference;
  const genericKnown = hasMeaningfulValue(match.entry.generic);
  const strength = hasMeaningfulValue(medicine.strength)
    ? medicine.strength
    : hasMeaningfulValue(reference?.strength)
      ? reference?.strength
      : medicine.strength;

  return {
    ...medicine,
    name: match.entry.brand,
    resolvedBrandName: match.entry.brand,
    generic_name: genericKnown ? match.entry.generic : medicine.generic_name,
    genericName: genericKnown ? match.entry.generic : medicine.genericName,
    form: hasMeaningfulValue(medicine.form) ? medicine.form : reference?.form || match.entry.form || medicine.form,
    strength,
    dosage: hasMeaningfulValue(medicine.dosage) ? medicine.dosage : reference?.dose || medicine.dosage,
    frequency: hasMeaningfulValue(medicine.frequency) ? medicine.frequency : reference?.frequencyPattern || medicine.frequency,
    timing: hasMeaningfulValue(medicine.timing) ? medicine.timing : reference?.foodTiming || medicine.timing,
    duration: hasMeaningfulValue(medicine.duration) ? medicine.duration : reference?.duration || medicine.duration,
    rawInstruction: [medicine.rawInstruction, reference?.rawLine].filter(Boolean).join(' '),
    instruction: hasMeaningfulValue(medicine.instruction) ? medicine.instruction : reference?.englishInstruction || medicine.instruction,
    englishInstruction: hasMeaningfulValue(medicine.englishInstruction) ? medicine.englishInstruction : reference?.englishInstruction || medicine.englishInstruction,
    banglaInstruction: hasMeaningfulValue(medicine.banglaInstruction) ? medicine.banglaInstruction : reference?.banglaInstruction || medicine.banglaInstruction,
    category: match.entry.category || medicine.category,
    commonUse: medicine.commonUse || match.entry.category || '',
    confidenceSource: `Training DB: ${match.entry.source || 'local verified dataset'}`,
    confidence: Math.max(medicine.confidence || 0, match.confidence),
    resolutionConfidence: match.confidence,
    genericConfirmed: genericKnown,
    verificationStatus: genericKnown ? 'known' : 'needs_verification',
    identificationStatus: genericKnown ? 'identified' : 'needs_review',
    possibleMatches: [],
  };
}
